import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, RefreshCw, Calendar, MapPin, User, Clock, Flag, X, Camera, Radio, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

/**
 * AlyNationalCalendarPage — Monthly calendar of National appointments.
 *
 *   Calendar View  →  click a date  →  Orders of That Day  →  click Order Number
 *   → opens the REAL mirrored order screen (/order/:orderId)
 *
 * Per user spec: no extra navigation layer, no summary screen. Clicking an order
 * number takes Aly directly to the full order context (photos, inspector, notes,
 * appointment info, inspection progress).
 */
const DOW = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const ymdLocal = (d) => {
  // Format a JS Date as YYYY-MM-DD in local time. Using toISOString would shift
  // the day on any non-UTC client, which is exactly what we must avoid here.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const parseApptDate = (o) => {
  // Orders may store the appointment date under several legacy keys and in two
  // formats (YYYY-MM-DD or MM/DD/YYYY). Normalise to YYYY-MM-DD or null.
  const raw = o.appointment_real_date || o.appointment_sg_date || o.scheduled_date || "";
  if (!raw) return null;
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return raw;
  const us = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (us) return `${us[3]}-${us[1].padStart(2, "0")}-${us[2].padStart(2, "0")}`;
  return null;
};

const AlyNationalCalendarPage = ({ Layout }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // Action card counters — polled live so Aly always knows what's pending
  const [uploadsPending, setUploadsPending] = useState(0);
  const [transmittedTotal, setTransmittedTotal] = useState(0);
  const [transmittedToday, setTransmittedToday] = useState(0);
  // Anchor month (first day)
  const [anchor, setAnchor] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [selectedDate, setSelectedDate] = useState(() => ymdLocal(new Date()));

  const fetchOrders = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/dispatch/orders`);
      setOrders(r.data || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load National orders");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Live counters for the two prominent action cards at the top.
  // Polls the existing endpoints every 30s. No extra backend work needed.
  useEffect(() => {
    let cancelled = false;
    const todayIso = new Date().toISOString().slice(0, 10);
    const refreshCounters = async () => {
      try {
        const [uploadsRes, portalRes] = await Promise.all([
          axios.get(`${API}/national-inspectors-upload-queue`).catch(() => ({ data: [] })),
          axios.get(`${API}/aly/my-transmissions`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setUploadsPending(Array.isArray(uploadsRes.data) ? uploadsRes.data.length : 0);
        if (portalRes.data?.summary) {
          setTransmittedTotal(portalRes.data.summary.total_transmitted || 0);
          // Count today's transmissions across all periods
          let todayCount = 0;
          for (const p of (portalRes.data.periods || [])) {
            for (const o of (p.orders || [])) {
              if ((o.transmission_date || "").startsWith(todayIso)) todayCount++;
            }
          }
          setTransmittedToday(todayCount);
        }
      } catch { /* silent */ }
    };
    refreshCounters();
    const iv = setInterval(refreshCounters, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // Only National orders with an appointment set + alive (not archived / not closed).
  const nationalAppointments = useMemo(() => {
    return (orders || [])
      .filter(o => (o.company || "").toLowerCase() === "national")
      .filter(o => o.appointment_set === "yes")
      .filter(o => o.removed_from_source !== "yes" && o.active_order !== "no")
      .map(o => ({ ...o, _apptDate: parseApptDate(o) }))
      .filter(o => !!o._apptDate);
  }, [orders]);

  // Group by YYYY-MM-DD
  const countsByDate = useMemo(() => {
    const m = new Map();
    for (const o of nationalAppointments) {
      m.set(o._apptDate, (m.get(o._apptDate) || 0) + 1);
    }
    return m;
  }, [nationalAppointments]);

  const selectedOrders = useMemo(() => {
    if (!selectedDate) return [];
    return nationalAppointments
      .filter(o => o._apptDate === selectedDate)
      .sort((a, b) => {
        const at = (a.appointment_real_time || a.appointment_sg_time || a.appointment_window || "").toString();
        const bt = (b.appointment_real_time || b.appointment_sg_time || b.appointment_window || "").toString();
        return at.localeCompare(bt);
      });
  }, [nationalAppointments, selectedDate]);

  // Build 6-week grid for the anchor month
  const grid = useMemo(() => {
    const firstOfMonth = new Date(anchor);
    const start = new Date(firstOfMonth);
    start.setDate(1 - firstOfMonth.getDay()); // back up to the Sunday of that week
    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push(d);
    }
    return days;
  }, [anchor]);

  const monthLabel = useMemo(() => anchor.toLocaleDateString("en-US", { month: "long", year: "numeric" }), [anchor]);
  const todayStr = ymdLocal(new Date());

  const shiftMonth = (delta) => {
    const d = new Date(anchor);
    d.setMonth(d.getMonth() + delta);
    setAnchor(d);
  };

  const openOrder = (orderId) => {
    if (!orderId) return;
    navigate(`/aly-order/${orderId}`);
  };

  const content = (
    <div className="space-y-5 p-6 max-w-[1400px] mx-auto" data-testid="aly-national-calendar">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-[#B08A3E]" />
            <h1 className="text-2xl font-bold text-[#1F2937]">National Calendar</h1>
            <span className="px-2 py-0.5 bg-[#B08A3E]/10 text-[#B08A3E] rounded-full text-[10px] font-bold uppercase tracking-wider">View only</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Click a date to see that day's National appointments. Click the order number to open the full order.
          </p>
        </div>
        <button
          onClick={fetchOrders}
          className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg text-sm text-gray-700"
          data-testid="aly-calendar-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {err && <p className="text-sm text-red-600 py-3 text-center bg-red-50 border border-red-100 rounded-lg">{err}</p>}

      {/* ═══════ TOP ACTION CARDS — Aly's unified hub ═══════
          Two very visible cards so nothing is hidden in a side menu.
          Left: Inspectors Upload (red badge when uploads are waiting).
          Right: Transmission (totals + today's count).
          Both are big click targets. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="aly-action-cards">
        {/* Inspectors Upload card */}
        <button
          type="button"
          onClick={() => navigate("/national-inspectors-upload")}
          className="group relative flex items-center gap-4 bg-white border-2 border-[#2F5D8A]/15 hover:border-[#2F5D8A] rounded-xl p-4 text-left transition-all shadow-sm hover:shadow-md"
          data-testid="aly-card-uploads"
        >
          <div className="relative flex items-center justify-center h-12 w-12 rounded-lg bg-[#2F5D8A]/10 text-[#2F5D8A] shrink-0">
            <Camera className="h-6 w-6" />
            {uploadsPending > 0 && (
              <span
                className="absolute -top-1.5 -right-1.5 min-w-[22px] h-5.5 px-1.5 rounded-full bg-[#C0392B] text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-white shadow animate-pulse"
                data-testid="aly-card-uploads-badge"
              >{uploadsPending > 99 ? "99+" : uploadsPending}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#2F5D8A]/70">Inspectors Upload</p>
            <p className="text-lg font-bold text-[#1F2937] leading-tight mt-0.5">
              {uploadsPending > 0 ? (
                <>{uploadsPending} waiting for review</>
              ) : (
                <>All caught up</>
              )}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {uploadsPending > 0 ? "Tap to review today's uploads" : "Today's inbox is clear"}
            </p>
          </div>
          {uploadsPending > 0 && <AlertCircle className="h-5 w-5 text-[#C0392B] shrink-0" />}
        </button>

        {/* Transmission card */}
        <button
          type="button"
          onClick={() => navigate("/aly-portal")}
          className="group relative flex items-center gap-4 bg-white border-2 border-[#6B5B8D]/20 hover:border-[#6B5B8D] rounded-xl p-4 text-left transition-all shadow-sm hover:shadow-md"
          data-testid="aly-card-transmission"
        >
          <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-[#6B5B8D]/12 text-[#6B5B8D] shrink-0">
            <Radio className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#6B5B8D]/80">Transmission</p>
            <p className="text-lg font-bold text-[#1F2937] leading-tight mt-0.5">
              {transmittedTotal} transmitted
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {transmittedToday > 0
                ? <>Today: <span className="font-semibold text-[#4A6B42]">{transmittedToday}</span> · tap to open portal</>
                : <>Tap to open transmission portal</>}
            </p>
          </div>
        </button>
      </div>

      {/* Month pager */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/60">
          <button onClick={() => shiftMonth(-1)} className="p-1.5 rounded hover:bg-gray-200 text-gray-600" data-testid="cal-prev-month">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <h2 className="text-sm font-bold uppercase tracking-widest text-[#1F2937]" data-testid="cal-month-label">{monthLabel}</h2>
          <button onClick={() => shiftMonth(1)} className="p-1.5 rounded hover:bg-gray-200 text-gray-600" data-testid="cal-next-month">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* DOW row */}
        <div className="grid grid-cols-7 bg-gray-50/40 border-b border-gray-100">
          {DOW.map(d => (
            <div key={d} className="px-2 py-2 text-center text-[11px] font-bold text-gray-600 uppercase tracking-wider">{d}</div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 auto-rows-[120px]">
          {grid.map((d, i) => {
            const ymd = ymdLocal(d);
            const inMonth = d.getMonth() === anchor.getMonth();
            const isToday = ymd === todayStr;
            const isSelected = ymd === selectedDate;
            const count = countsByDate.get(ymd) || 0;
            const isFuture = ymd > todayStr;
            return (
              <button
                type="button"
                key={i}
                onClick={() => setSelectedDate(ymd)}
                className={`relative border border-gray-100 p-2 text-left transition-all hover:bg-[#F5F1E8]/40 ${
                  !inMonth ? "bg-gray-50/40 text-gray-300" : ""
                } ${isSelected ? "ring-2 ring-inset ring-[#B08A3E] bg-[#FDF5E7]/50" : ""}`}
                data-testid={`cal-day-${ymd}`}
              >
                <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-bold tracking-tight ${
                  isToday ? "bg-[#4F7046] text-white"
                  : isFuture ? "bg-[#7FDCDD]/70 text-[#1F5E62]"
                  : inMonth ? "bg-[#FAF3D0] text-[#7A6010]"
                  : "bg-transparent text-gray-300"
                }`}>{ymd.slice(5)}</span>
                {count > 0 && (
                  <p className="mt-1 text-[11px] font-bold text-[#C25757]" data-testid={`cal-day-count-${ymd}`}>
                    {count} {count === 1 ? "Appt" : "Appts"}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Day orders panel */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="cal-day-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-[#FDF5E7]/40">
          <div className="flex items-center gap-2">
            <Flag className="h-4 w-4 text-[#B08A3E]" />
            <h3 className="text-sm font-bold text-[#1F2937]">
              Orders on {selectedDate}
            </h3>
            <span className="px-2 py-0.5 bg-[#B08A3E]/12 text-[#8A6A2F] text-[10px] font-bold rounded-full">
              {selectedOrders.length} {selectedOrders.length === 1 ? "order" : "orders"}
            </span>
          </div>
          {selectedDate && selectedDate !== todayStr && (
            <button onClick={() => setSelectedDate(todayStr)} className="text-[11px] text-[#2F5D8A] hover:underline" data-testid="cal-jump-today">
              Jump to today
            </button>
          )}
        </div>
        {selectedOrders.length === 0 ? (
          <div className="p-8 text-center">
            <Calendar className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No National appointments on {selectedDate}.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50/70">
                <tr className="text-left">
                  <th className="px-3 py-2 font-semibold text-gray-500 uppercase text-[10px] tracking-wider">Appt Time</th>
                  <th className="px-3 py-2 font-semibold text-gray-500 uppercase text-[10px] tracking-wider">Order #</th>
                  <th className="px-3 py-2 font-semibold text-gray-500 uppercase text-[10px] tracking-wider">Inspector</th>
                  <th className="px-3 py-2 font-semibold text-gray-500 uppercase text-[10px] tracking-wider">Client</th>
                  <th className="px-3 py-2 font-semibold text-gray-500 uppercase text-[10px] tracking-wider">Address</th>
                  <th className="px-3 py-2 font-semibold text-gray-500 uppercase text-[10px] tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {selectedOrders.map(o => {
                  const when = o.appointment_real_time || o.appointment_sg_time || o.appointment_window || "—";
                  return (
                    <tr key={o.id || o.order_number} className="border-t border-gray-100 hover:bg-gray-50/40" data-testid={`cal-day-row-${o.order_number}`}>
                      <td className="px-3 py-2 text-gray-700 whitespace-nowrap flex items-center gap-1">
                        <Clock className="h-3 w-3 text-gray-400" /> {when}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => openOrder(o.id)}
                          disabled={!o.id}
                          className="font-mono font-bold text-[#2F5D8A] hover:underline hover:text-[#1F3F60] disabled:text-gray-400 disabled:cursor-not-allowed"
                          data-testid={`cal-open-order-${o.order_number}`}
                          title={o.id ? "Open full order" : "Order id missing"}
                        >
                          {o.order_number}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-gray-700 flex items-center gap-1 whitespace-nowrap">
                        <User className="h-3 w-3 text-gray-400" />
                        {o.assigned_inspector || <span className="text-gray-400 italic">unassigned</span>}
                      </td>
                      <td className="px-3 py-2 text-gray-600 truncate max-w-[140px]" title={o.client || o.cliente}>{o.client || o.cliente || "—"}</td>
                      <td className="px-3 py-2 text-gray-600 min-w-[180px]">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{o.address}{o.city ? `, ${o.city}` : ""}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-600">{o.status || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  return Layout ? <Layout>{content}</Layout> : content;
};

export default AlyNationalCalendarPage;
